import { useCallback, useContext, useEffect, useState } from "react";
import { useEditCandidateProfileMutation } from "../../../../App/Queries/CandidateQueries";
import { validateEmail } from "../../../../App/Scripts/emailValidator";
import { ICandidateEmailAddress, IEditCandidateEmailAddress } from "../../../../App/Typings/Candidate";
import { CandidateContext } from "../../../CandidateProfile";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { CandidateEmailType } from "../../../../App/Enums/CandidateEnums";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";

export const useEditEmailState = (candidateEmailAddress: ICandidateEmailAddress) => {
  const { candidate } = useContext(CandidateContext);
  const { displaySuccess, displayError, displayInfo } = useNotification();
  const { mutateAsync: editProfileAsync, isLoading } = useEditCandidateProfileMutation();
  const { id, emailAddress, emailAddressType } = candidateEmailAddress;
  const [isEditEmail, setIsEditEmail] = useState(false);
  const [editEmail, setEditEmail] = useState<IEditCandidateEmailAddress>({
    emailAddressId: id,
    emailAddress,
    emailType: CandidateEmailType[emailAddressType],
  });

  // Update state when id gets updated after number is added
  useEffect(() => {
    setEditEmail((prev) => ({
      ...prev,
      emailAddressId: id,
    }));
  }, [id]);

  const validEmailAddressEdit = validateEditEmailAddress(editEmail);
  const changedEmailAddress = editEmail.emailAddress !== emailAddress;
  const changedEmailType = editEmail.emailType !== CandidateEmailType[emailAddressType];
  const disableSave = isLoading || !validEmailAddressEdit || (!changedEmailAddress && !changedEmailType);
  let saveButtonMessage = "Save Changes";
  if (isLoading) {
    saveButtonMessage = "Saving...";
  } else if (!validEmailAddressEdit) {
    saveButtonMessage = "Invalid email address";
  } else if (!changedEmailAddress && !changedEmailType) {
    saveButtonMessage = "No changes made to email address or type.";
  }

  function handleChangeEmailAddress(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setEditEmail((edit) => ({ ...edit, emailAddress: e.target.value }));
  }
  function handleChangeEmailType(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setEditEmail((edit) => ({
      ...edit,
      emailType: CandidateEmailType[Number(e.target.value)],
    }));
  }

  const submitEditEmail = useCallback(async () => {
    if (disableSave) {
      handleCancelChanges();
      displayInfo("No changes made to email address or type.");
      return;
    }
    await editProfileAsync(
      {
        candidateId: candidate.id,
        emailAddress: editEmail,
        phoneNumber: null,
        tags: null,
      },
      {
        onError: () => {
          displayError("Error updating email address");
        },
        onSettled: (response) => {
          if (response?.success === true) {
            setIsEditEmail(false);
            displaySuccess("Successfully updated email address");
          } else if (response?.errorMessage) {
            displayError(response?.errorMessage);
          } else {
            displayError("There was an error updating email address");
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEmail.emailAddress, editEmail.emailType]);

  const handleCancelChanges = () => {
    setEditEmail({
      emailAddressId: id,
      emailAddress,
      emailType: CandidateEmailType[emailAddressType],
    });
    setIsEditEmail(false);
  };

  const handleSubmit = useDebouncedHandler(() => {
    submitEditEmail();
  }, 300);

  return {
    getters: {
      isEditEmail,
      editEmail,
      emailAddress,
      emailAddressType,
      disableSave,
      saveButtonMessage,
      validEmailAddressEdit,
    },
    handlers: {
      setIsEditEmail,
      setEditEmail,
      handleChangeEmailAddress,
      handleChangeEmailType,
      handleCancelChanges,
      handleSubmit,
    },
  } as const;
};

function validateEditEmailAddress(editEmail: IEditCandidateEmailAddress): boolean {
  if (!editEmail.emailAddress) {
    return false;
  }
  return Boolean(validateEmail(editEmail.emailAddress));
}

export const emailAddressTypeOptions = Object.keys(CandidateEmailType)
  .filter((key) => Number(key) >= 0)
  .map((key) => ({
    value: key,
    label: CandidateEmailType[key as keyof typeof CandidateEmailType],
  }));

export function convertStringToCandidateEmailType(emailAddressType: string): CandidateEmailType {
  switch (emailAddressType) {
    case "Personal":
      return CandidateEmailType.Personal;
    case "Work":
      return CandidateEmailType.Work;
    case "Other":
      return CandidateEmailType.Other;
    default:
      return CandidateEmailType.Other;
  }
}
